import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import GuideComparisonTable from './GuideComparisonTable';
import GuideSpeedFigure from './GuideSpeedFigure';
import usePageMeta from '../hooks/usePageMeta';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import { trackEvent } from '../analytics';
import { MAX_SPEED_MIN, MAX_SPEED_MAX } from '../lib/motionPace';
import './SeoGuideCluster.css';

// "How to make a variable speed timelapse" — the how-to page of the guide
// cluster (#547). The pillar (/guides/what-is-adaptive-pacing) defines the
// term; this page answers the query people actually type.
//
// WHY IT TEACHES THE COMPETITION. "Variable speed timelapse" is a how-to query:
// the searcher wants to make one, not to buy something. A page that answers it
// with "use our product" loses to any page that answers it properly, so this
// one walks all three real methods — in camera, by hand in an editor, and
// automatically from the footage — and states the honest limit of the
// in-camera and editor routes. Manual editing is framed as the route with the
// most control when you need it — never as the best approach. The product
// appears as the third method's implementation, after the technique has been
// explained, and links to /motionpace (#544).
//
// AUDIENCE: contractors and DIY builders, not film people. "Keyframes" earns
// its place because it is the editor method's real name; nothing else does.
// Every sentence has to survive being read on a phone in a truck.
//
// GATED AT BUILD TIME, exactly like /motionpace and the pillar. This component
// knows nothing about that: the route is only declared when the marketing flag
// is set, so nothing here may read a runtime flag — the page must render
// identically for a crawler with no JavaScript and no session.
//
// COPY RULES ARE PRODUCT DECISIONS, NOT STYLE (#544/#546), and
// VariableSpeedTimelapsePage.test.js is the regression net for each:
//   - The brand is MotionPace. The technique is "adaptive pacing", and a video
//     made with it is "an adaptively paced timelapse" — never the noun stack,
//     never a coined category name.
//   - "Nothing cut out", never "every frame kept": a paced render drops frames
//     to build the output, it just does not delete any stretch of the shoot.
//   - The retired dead-* vocabulary (#379) stays out; the shared noun is
//     "stillness" / "the still stretches".
//   - The speed ceiling is read from the engine, never typed in.
//   - The optional speed dial's default state is still undecided, so the page
//     may not claim it is on or off out of the box.
//   - The editor route is "the most control", never "the best": superlative
//     quality claims for any method stay out.

// The Max speed range, READ FROM THE MODULE THAT CLAMPS IT. lib/motionPace.js
// mirrors the server-side clamps in shared/motion_pace.py, so a number derived
// from them cannot outlive the limit it describes.
const MAX_SPEED_RANGE = { min: String(MAX_SPEED_MIN), max: String(MAX_SPEED_MAX) };
const MAX_SPEED_RANGE_LABEL = `${MAX_SPEED_RANGE.min}× to ${MAX_SPEED_RANGE.max}×`;

const CANONICAL_PATH = '/guides/variable-speed-timelapse';
const PAGE_TITLE = 'How to Make a Variable Speed Timelapse | Tweedia Studio';
const PAGE_DESCRIPTION =
  'A variable speed timelapse slows down for the action and speeds through the still ' +
  'stretches. Three ways to make one — in camera, by hand with speed ramps in a video ' +
  'editor, or automatically from the footage — and how to pick.';

// THE answer, written once. The visible callout, the meta description's promise
// and the first FAQ entry all quote this constant, so the page cannot end up
// answering its own query two different ways.
const SHORT_ANSWER =
  'A variable speed timelapse is one whose playback speed changes as it goes: slow while ' +
  'something is happening, fast through the still stretches. There are three ways to make ' +
  'one — let the camera vary the speed while you shoot, ramp the speed by hand with ' +
  'keyframes in a video editor, or let software read the footage and set the speed for you.';

const TOC = [
  { id: 'vs_answer', label: 'The short answer' },
  { id: 'vs_methods', label: 'The three ways to make one' },
  { id: 'vs_camera', label: 'Method 1: In camera, while you shoot' },
  { id: 'vs_editor', label: 'Method 2: By hand in a video editor' },
  { id: 'vs_auto', label: 'Method 3: Automatically, from the footage' },
  { id: 'vs_choose', label: 'Which method should you use?' },
  { id: 'vs_motionpace', label: 'Making one in Tweedia Studio' },
  { id: 'vs_faq', label: 'FAQ' },
];

// Derived rather than hand-listed: a section added to the contents must also be
// reported to analytics, and two lists would drift.
const SECTION_IDS = TOC.map((item) => item.id);

// The comparison the page is quoted for. The column that decides most arguments
// is the third one — whether the method still applies to footage already shot —
// so it sits in the middle where a narrow screen still shows it.
//
// The headers live beside the data because GuideComparisonTable writes each one
// onto its own cell as `data-label`, which is what the phone layout prints above
// the value. A column renamed here is renamed in both layouts at once.
const METHOD_COLUMNS = [
  { key: 'label', header: 'Method' },
  { key: 'driver', header: 'What sets the speed' },
  { key: 'existing', header: 'Works on footage you already shot?' },
  { key: 'effort', header: 'Effort' },
  { key: 'bestFor', header: 'Best for' },
];

const METHODS = [
  {
    id: 'camera',
    label: 'In camera, while you shoot',
    driver: 'The camera, from a timer it stretches as the recording runs on',
    existing: 'No — it is baked in at capture',
    effort: 'Low: pick a mode before you start',
    bestFor: 'Short clips you are shooting right now',
  },
  {
    id: 'editor',
    label: 'By hand in a video editor',
    driver: 'You, one keyframe at a time',
    existing: 'Yes',
    effort: 'High: you must watch the raw footage first, then place every ramp',
    bestFor: 'Short hero clips you want to control second by second',
  },
  {
    id: 'auto',
    label: 'Automatically, from the footage',
    // The only linked label in this table, and so the only teal one: across the
    // cluster teal now means "this goes somewhere" and nothing else. It goes to
    // the page that defines the technique.
    to: '/guides/what-is-adaptive-pacing',
    highlight: true,
    driver: 'The footage — how much moved between each pair of frames',
    existing: 'Yes',
    effort: 'Low: one switch, then a ceiling to taste',
    bestFor: 'Long fixed-camera shoots where the work comes in bursts',
  },
];

// The honest limit of the in-camera and editor methods. The automatic method
// carries no catch card, so the page frames the choice as control vs scale
// rather than good vs best — but it does still state the limit: CHOOSE_MANUAL
// below ("a quiet one that no movement score would notice") is where it lives
// now, and it is the line to edit if that limit needs restating. Not the pillar
// page: AdaptivePacingPage's WHEN_IT_DOES_NOT list covers when adaptive pacing
// is the wrong FIT (steady-rate subjects, clips short enough to watch straight
// through, moving cameras), never what a movement score cannot see.
const METHOD_CATCHES = [
  {
    id: 'camera',
    title: 'The catch: it is baked in at capture',
    body:
      'The camera writes one finished video and only that video. The frames it skipped were ' +
      'never saved, so there is no going back afterwards to slow a moment down — you would be ' +
      'stretching frames that do not exist. And the rate it picks is a timer, not a judgment: ' +
      'it knows how long the recording has run, not what is happening in front of the lens, so ' +
      'the busy hour and the empty one come out compressed exactly alike.',
  },
  {
    id: 'editor',
    title: 'The catch: it does not scale',
    body:
      'On a twenty-second clip this works well — every ramp is one you placed on purpose. The ' +
      'problem is finding the moments. To place a ramp you have to know where the interesting ' +
      'parts are, and the only way to know is to watch the raw footage — days of scrubbing on ' +
      'a four-month jobsite shoot before you touch a single keyframe, and most of the ramps ' +
      'move if you re-cut anything. This is the single most common reason long timelapse ' +
      'footage sits on a drive, unedited, forever.',
  },
];

// The five clicks that actually make a speed ramp, named the way each editor
// names them. Generic "add keyframes" advice is the reason people bounce off
// tutorials, so the menu item is spelled out.
const EDITOR_STEPS = [
  'Put the timelapse on the timeline and scrub it once, marking the moments worth slowing ' +
    'down.',
  'Open the clip’s speed curve — Time Remapping in Premiere Pro, the Retime Curve in ' +
    'DaVinci Resolve, Retime in Final Cut Pro.',
  'Add a keyframe just before and just after each of those moments.',
  'Set the speed low between the pair of keyframes, and high across the stretch between one ' +
    'moment and the next.',
  'Drag the handles on each keyframe apart so the change eases in over a second or so ' +
    'instead of snapping.',
];

// The Studio path, as a procedure rather than a pitch. Step 3 carries the only
// number on the page, and it is read from the engine.
const STUDIO_STEPS = [
  {
    title: 'Upload the shoot.',
    body:
      'A video file or a folder of stills, from any camera — a jobsite camera, a GoPro, a ' +
      'phone on a windowsill, a DSLR on an intervalometer.',
  },
  {
    title: 'Switch MotionPace on for that video.',
    body: 'There is nothing to mark up and no timeline to drag.',
  },
  {
    title: 'Set the ceiling.',
    body:
      `Max speed is how fast the still stretches are allowed to get: anywhere from ${MAX_SPEED_RANGE_LABEL} ` +
      'the base speed. Near the bottom it reads as a gentle drift; near the top the quiet ' +
      'hours genuinely fly. A Smoothness setting decides how gradually each change arrives.',
  },
  {
    title: 'Watch the preview, then render.',
    body:
      'The paced preview updates before you render, so you can try a setting without spending ' +
      'anything. Credits are charged on the finished video.',
  },
];

const CHOOSE_AUTO = [
  'A camera sat in one place for weeks or months — a build, a remodel, a barn raise.',
  'The interesting part is a small slice of the recording and the rest is nights, weekends ' +
    'and weather delays.',
  'The footage is already on a card and you are not going back to re-shoot it.',
  'You want it finished today, not over a weekend of scrubbing.',
];

// Three reasons, not four: the fourth was "the camera has a speed-ramp mode",
// which only ever referred to TimeWarp's hold-a-button ramp. With that guide
// gone the page describes no camera that ramps on demand, so the line pointed at
// a capability the reader is never shown.
const CHOOSE_MANUAL = [
  'The clip is short enough to watch straight through, and you want a hand on every ramp.',
  'The moment you want to hold on is a quiet one that no movement score would notice.',
  'You are cutting to music and every speed change has to land on a beat.',
];

// THE single source for the FAQ: it renders the visible section AND feeds the
// FAQPage markup, so the two can never disagree. A rich-text answer would force
// a second hand-kept plain-text copy for the structured data, which is exactly
// how those drift apart.
const FAQ = [
  {
    q: 'What is a variable speed timelapse?',
    a: SHORT_ANSWER,
  },
  {
    q: 'Can I change the speed of a timelapse I already shot?',
    a:
      'Yes, as long as the camera recorded at one steady rate — a normal timelapse clip or a ' +
      'folder of stills. You can ramp it by hand in any video editor, or have software do it ' +
      'from the footage. What you cannot undo is a camera that already varied the speed for ' +
      'you: that decision was written into the file at capture and the skipped frames were ' +
      'never saved.',
  },
  {
    q: 'How do I make a speed ramp in a video editor?',
    a:
      'Open the clip’s speed curve — Time Remapping in Premiere Pro, the Retime Curve in ' +
      'DaVinci Resolve, Retime in Final Cut Pro — then add a keyframe either side of each ' +
      'moment you want slowed, set a low speed between that pair and a high speed across the ' +
      'stretches in between, and drag the keyframe handles apart so the change eases in ' +
      'instead of snapping.',
  },
  {
    q: 'Does varying the speed cut footage out?',
    a:
      'No — nothing cut out. The whole shoot is still in the video from first frame to last; ' +
      'the still stretches simply go past faster. Cutting those stretches away entirely is a ' +
      'different edit, usually called trimming.',
  },
  {
    q: 'How much faster should the quiet parts play?',
    a:
      `That is your call. In Tweedia Studio you set a ceiling anywhere from ${MAX_SPEED_RANGE_LABEL} ` +
      'the base speed, and the busy stretches settle back to the base speed no matter where ' +
      'the ceiling sits. Start low on a shoot where small changes matter, and push it up on a ' +
      'shoot with long quiet runs.',
  },
  {
    q: 'Do I need a special camera for it?',
    a:
      'Only for the in-camera method. The other two happen in the edit, so any footage works ' +
      '— a jobsite camera, a GoPro, a phone on a windowsill, or a folder of stills from a ' +
      'DSLR.',
  },
  {
    q: 'What is a timelapse made this way called?',
    a:
      'It depends which method made it. When the footage sets the speed, the technique is ' +
      'adaptive pacing and the result is an adaptively paced timelapse. When you place the ' +
      'changes yourself, the changes are called speed ramps. Either way the file is a normal ' +
      'MP4 you can post anywhere.',
  },
];

function buildJsonLd() {
  const url = `https://tweediastudio.com${CANONICAL_PATH}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: 'How to Make a Variable Speed Timelapse',
        description: PAGE_DESCRIPTION,
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Tweedia Studio', url: 'https://tweediastudio.com' },
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
        image: 'https://tweediastudio.com/tweedia_tour_poster.jpg',
      },
      // The node the query asks for. The three "steps" are the three ROUTES —
      // alternatives, not a sequence — which the description says plainly so
      // the markup cannot be read as "do all three".
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: 'How to Make a Variable Speed Timelapse',
        description:
          'Three routes to a timelapse whose speed follows the action. They are alternatives: ' +
          'pick the one that matches your shoot, starting from whether the footage is already ' +
          'in the can.',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Vary the speed in camera, while you shoot',
            url: `${url}#vs_camera`,
            text:
              'Shoot in a mode that varies the rate for you, such as a phone time-lapse mode. ' +
              'Fast to set up, but the pacing is fixed at capture and cannot be changed ' +
              'afterwards.',
          },
          {
            '@type': 'HowToStep',
            name: 'Ramp the speed by hand in a video editor',
            url: `${url}#vs_editor`,
            text:
              'Open the clip’s speed curve, add keyframes either side of each moment worth ' +
              'slowing, set a low speed between them and a high speed across the stretches in ' +
              'between, then ease each change in. Total control; impractical past a short clip.',
          },
          {
            '@type': 'HowToStep',
            name: 'Let the footage set the speed automatically',
            url: `${url}#vs_auto`,
            text:
              'Have software score the movement between frames and turn those scores into a ' +
              'speed for every moment — adaptive pacing. Works on footage already shot, with ' +
              'nothing cut out, and needs no pass through the raw material.',
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function VariableSpeedTimelapsePage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonicalPath: CANONICAL_PATH,
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const setSectionRef = useSectionViewTracking(SECTION_IDS);

  const handleStudioCta = () => {
    trackEvent('cta_click', { cta_id: 'variable_speed_try_studio', signed_in: signedIn });
    if (!signedIn) signIn();
  };

  const trackNav = (target) => () => {
    trackEvent('nav_click', { target });
  };

  const methodCatch = (id) => {
    const note = METHOD_CATCHES.find((item) => item.id === id);
    if (!note) return null;
    return (
      <div className="gc-note" data-testid={`variable-speed-catch-${note.id}`}>
        <h3>{note.title}</h3>
        <p>{note.body}</p>
      </div>
    );
  };

  const studioCta = signedIn ? (
    <Link
      to="/studio"
      className="gc-primary-cta"
      data-testid="variable-speed-studio-cta"
      onClick={handleStudioCta}
    >
      Try it on your own footage
    </Link>
  ) : (
    <button
      type="button"
      className="gc-primary-cta"
      data-testid="variable-speed-studio-cta"
      onClick={handleStudioCta}
    >
      Try it on your own footage
    </button>
  );

  return (
    <div className="gc-page">
      <Header user={user} signIn={signIn} />
      <main className="gc-content" data-testid="variable-speed-content">
        <p className="gc-eyebrow">Guide</p>
        <h1>How to Make a Variable Speed Timelapse</h1>
        <p className="gc-meta">
          Three methods, what each one costs you, and how to pick. 7 min read.
        </p>

        {/* The answer, above everything else. This block is what a search
            snippet and an answer engine quote, so it is short, complete on its
            own, and repeated nowhere in a different form. */}
        <div className="gc-answer" data-testid="variable-speed-answer">
          <p className="gc-answer-label">Short answer</p>
          <p className="gc-answer-text">{SHORT_ANSWER}</p>
          <p className="gc-answer-note">
            One question narrows it fast: <strong>has the footage already been shot?</strong> If it
            has, the in-camera route is off the table.
          </p>
        </div>

        <p className="gc-lede">
          A timelapse that runs at one speed spends the same slice of your video on the morning
          the crane showed up and the afternoon it rained and nobody came. Varying the speed
          fixes that: the work gets room to breathe, the quiet stretches go past quickly, and the
          shoot stays whole. This page walks all three ways to do it — including the two that
          have nothing to do with our software — and says what each one is bad at.
        </p>

        <nav className="gc-toc" aria-label="Table of contents">
          <p className="gc-toc-title">Contents</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="vs_answer" data-section-id="vs_answer" ref={setSectionRef('vs_answer')}>
          <h2>The short answer</h2>
          <p>
            Picture the speed of a timelapse as a dial. On a normal one it is glued in a single
            position for the whole clip. Making a variable speed timelapse means unsticking that
            dial and deciding — or letting something else decide — where it sits moment by
            moment. Done well the changes are gradual, so on screen it reads as the video
            breathing rather than as a cut.
          </p>
          <p>
            So the real question is not <em>how</em> so much as <em>who turns the dial</em>: your
            camera while you shoot, you at a keyboard afterwards, or software reading the footage.
            Those are the three methods, and they are not interchangeable — the first one is only
            available before you press record.
          </p>
        </section>

        <section id="vs_methods" data-section-id="vs_methods" ref={setSectionRef('vs_methods')}>
          <h2>The three ways to make one</h2>
          <p>
            Side by side, on the four things that decide which one you can actually use. The
            middle column settles most arguments on its own.
          </p>

          {/* Shared with the pillar and the other three guides: scrollable
              table on a desktop, one card per method on a phone. */}
          <GuideComparisonTable
            columns={METHOD_COLUMNS}
            rows={METHODS}
            ariaLabel="Comparison of ways to make a variable speed timelapse"
            testId="variable-speed-comparison"
            onRowLinkClick={() => trackEvent('nav_click', {
              target: 'adaptive_pacing_from_variable_speed_table',
            })}
          />

          {/* The argument as a picture: a flat line against a curve. */}
          <GuideSpeedFigure variant="variableSpeed" />

          <p>
            The rest of the page takes each one in turn: how you actually do it, and the catch
            nobody mentions until you are halfway through.
          </p>
        </section>

        <section id="vs_camera" data-section-id="vs_camera" ref={setSectionRef('vs_camera')}>
          <h2>Method 1: In camera, while you shoot</h2>
          <p>
            Some cameras vary the speed for you as they record. Phone time-lapse modes are the
            common version: an iPhone keeps stretching its own capture interval as a recording
            runs on, so a one-hour and a four-hour shoot both come out roughly the same length.
            That is the camera changing the speed — but it changes it for the whole clip, not for
            the interesting parts of it.
          </p>
          <p>
            <strong>How to do it:</strong> on a phone, open the time-lapse mode and hit record.
            That is the entire procedure, and that is the appeal.
          </p>

          {methodCatch('camera')}
        </section>

        <section id="vs_editor" data-section-id="vs_editor" ref={setSectionRef('vs_editor')}>
          <h2>Method 2: By hand in a video editor</h2>
          <p>
            This is the classic answer, and every editor has it: a speed ramp, also called time
            remapping. You put the clip on a timeline, drop keyframes onto its speed, tell the
            editor what the speed should be at each one, and then smooth each change so it eases
            in rather than jumping — a bare keyframe is an instant switch, not a blend. Premiere
            Pro, DaVinci Resolve, Final Cut Pro and CapCut all do it. The menus differ; the idea
            does not.
          </p>
          <ol className="gc-steps" data-testid="variable-speed-editor-steps">
            {EDITOR_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p>
            This is the method that gives you the most control. You are placing every change
            deliberately, so the video lands exactly where you want it to and nothing surprises
            you.
          </p>

          {methodCatch('editor')}
        </section>

        <section id="vs_auto" data-section-id="vs_auto" ref={setSectionRef('vs_auto')}>
          <h2>Method 3: Automatically, from the footage</h2>
          <p>
            The third route removes the part that does not scale: instead of you finding the busy
            moments, software finds them. It compares each frame with the one before it and
            scores how much of the picture actually changed, smooths those scores so a passing
            truck or a cloud shadow does not throw it, then turns the result into a speed for
            every moment of the clip. Busy stretches settle back to the base speed. Quiet ones
            climb toward a ceiling you choose.
          </p>
          <p>
            The technique has a name — this is{' '}
            <Link
              to="/guides/what-is-adaptive-pacing"
              data-testid="variable-speed-adaptive-pacing-link"
              onClick={trackNav('adaptive_pacing_from_variable_speed')}
            >
              what adaptive pacing is
            </Link>{' '}
            — and a video made this way is called an adaptively paced timelapse. What separates
            it from the other two methods in practice: hundreds of speed changes end up in the
            clip, you placed none of them, and you never had to watch the raw footage to find out
            where they belonged.
          </p>
          <p>
            There is nothing cut out, either. The rained-out week is still in the video from
            first frame to last; it just goes by in a couple of seconds instead of a couple of
            minutes. Cutting those stretches away entirely is a different edit, usually called
            trimming.
          </p>
        </section>

        <section id="vs_choose" data-section-id="vs_choose" ref={setSectionRef('vs_choose')}>
          <h2>Which method should you use?</h2>
          <p>Two questions get you there.</p>
          <ul>
            <li>
              <strong>Is the footage already shot?</strong> If it is, cross off method 1. Pacing
              is only available in camera before you press record.
            </li>
            <li>
              <strong>How long is the raw shoot?</strong> Short enough to watch straight through,
              doing it by hand is practical and puts every ramp under your control. Hours, weeks
              or months, do it automatically, because nobody watches four months of footage to
              find the good parts.
            </li>
          </ul>
          <div className="gc-fit">
            <div className="gc-fit-col gc-fit-yes" data-testid="variable-speed-fit-auto">
              <h3>Let the footage set it</h3>
              <ul>
                {CHOOSE_AUTO.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="gc-fit-col gc-fit-no" data-testid="variable-speed-fit-manual">
              <h3>Set it yourself</h3>
              <ul>
                {CHOOSE_MANUAL.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p>
            Still planning the shoot rather than editing it? Start with{' '}
            <Link
              to="/guides/long-term-timelapse"
              data-testid="variable-speed-longterm-link"
              onClick={trackNav('long_term_guide_from_variable_speed')}
            >
              the long-term timelapse guide
            </Link>
            . Interval, power and framing decide far more about the finished video than pacing
            ever will. Pacing is the last decision, not the first.
          </p>
        </section>

        <section
          id="vs_motionpace"
          data-section-id="vs_motionpace"
          ref={setSectionRef('vs_motionpace')}
        >
          <h2>Making one in Tweedia Studio</h2>
          <p>
            <strong>MotionPace</strong> is Tweedia Studio&apos;s implementation of the third
            method. It runs on a shoot you have already filmed — a clip off a jobsite camera, or
            a folder of stills — and it is four steps end to end.
          </p>
          <ol className="gc-steps" data-testid="variable-speed-studio-steps">
            {STUDIO_STEPS.map((step) => (
              <li key={step.title}>
                <strong>{step.title}</strong> {step.body}
              </li>
            ))}
          </ol>
          <p>
            The ceiling is the only number you need an opinion about, and it only ever governs
            how fast the quiet gets: wherever you put it, the busy stretches settle back to the
            base speed.
          </p>
          <div className="gc-callout">
            <h3>See it on real footage</h3>
            <p>
              The MotionPace page charts an actual deck build — days of a fixed camera, paced
              automatically, with nothing cut out — against the same footage at a constant speed.
            </p>
            <Link
              to="/motionpace"
              className="gc-callout-cta"
              data-testid="variable-speed-motionpace-link"
              onClick={trackNav('motionpace_from_variable_speed')}
            >
              How MotionPace works →
            </Link>
          </div>
        </section>

        <section id="vs_faq" data-section-id="vs_faq" ref={setSectionRef('vs_faq')}>
          <h2>FAQ</h2>
          <div className="gc-faq" data-testid="variable-speed-faq">
            {FAQ.map((item) => (
              <div className="gc-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="gc-final-cta" data-testid="variable-speed-final-cta">
          <h2>Try it on a shoot you have already filmed</h2>
          <p>
            Upload footage you filmed months ago, let the movement in it set the pace, and watch
            a draft before you spend a credit.
          </p>
          <div className="gc-final-cta-row">
            {studioCta}
            <Link
              to="/motionpace"
              className="gc-secondary-cta"
              data-testid="variable-speed-final-motionpace-link"
              onClick={trackNav('motionpace_from_variable_speed_cta')}
            >
              See MotionPace
            </Link>
          </div>
          <p className="gc-note-fine">
            Tuning and previewing are always free. Credits are charged on a finished render.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default VariableSpeedTimelapsePage;
export {
  SHORT_ANSWER,
  FAQ,
  TOC,
  METHODS,
  METHOD_COLUMNS,
  METHOD_CATCHES,
  EDITOR_STEPS,
  STUDIO_STEPS,
  MAX_SPEED_RANGE,
  CANONICAL_PATH,
  PAGE_TITLE,
  buildJsonLd,
};
